import { ParsedProspect } from "../parser";

import { MULTI_SELECT_FIELD_IDS } from "./model";

export const BLANK_OPTION = "(Blank)";

/**
 * Canonical display order for tier-style fields. Values come from the
 * questionnaire form; alphabetical sort would order them incorrectly
 * ("Greater than 100k" before "Greater than 5k"). Fields not listed here
 * fall back to alphabetical sort.
 *
 * Values present in the data but missing from this list are still
 * selectable — they're appended after the known tiers, before `(Blank)`.
 */
export const TIER_OPTION_ORDER: Readonly<Record<string, readonly string[]>> = {
  averageAnnualVolume: [
    "Less than 5k",
    "Greater than 5k, less than 10k",
    "Greater than 10k, less than 30k",
    "Greater than 30k, less than 50k",
    "Greater than 50k, less than 100k",
    "Greater than 100k, less than 200k",
    "Greater than 200k, less than 500k",
    "Greater than 500k, less than 1m",
    "Greater than 1m, less than 5m",
    "Greater than 5m, less than 10m",
    "Greater than 10m, less than 20m",
    "Greater than 20m, less than 50m",
    "Greater than 50m, less than 100m",
    "100m or more",
  ],
};

export type MultiSelectData = {
  /** sorted, dedup'd option lists per multi-select field */
  optionsByField: Map<string, string[]>;
  /** raw questionnaire answers per row (id → field → raw value) */
  rawValuesByRowId: Map<string, Record<string, unknown>>;
};

/**
 * Returns the raw answer for a known multi-select questionnaire field on
 * a parsed prospect, or `undefined` if the field isn't present. The raw
 * shape is preserved (string for scalar answers, string[] for multi-value
 * answers) so downstream code doesn't have to reverse-engineer it from
 * the formatted display string.
 */
const readRawAnswer = (
  prospect: ParsedProspect,
  field: string,
): unknown | undefined => {
  const answers = prospect.questionaireAnswers;
  if (!answers || typeof answers !== "object") return undefined;
  const entry = (answers as Record<string, unknown>)[field];
  if (entry === undefined || entry === null) return entry;
  if (typeof entry === "object" && entry !== null && "answer" in entry) {
    return (entry as { answer: unknown }).answer;
  }
  return entry;
};

const isBlank = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

/**
 * Sort options for a field. Tier fields use their canonical order; values
 * not in the canonical list are appended (alphabetically). Other fields
 * are alphabetical. `(Blank)` always comes last when present.
 */
const sortOptions = (field: string, values: Set<string>): string[] => {
  const hasBlank = values.has(BLANK_OPTION);
  const withoutBlank = new Set(values);
  withoutBlank.delete(BLANK_OPTION);

  const tierOrder = TIER_OPTION_ORDER[field];
  let sorted: string[];
  if (tierOrder) {
    const known: string[] = [];
    for (const tier of tierOrder) {
      if (withoutBlank.delete(tier)) known.push(tier);
    }
    const unknown = [...withoutBlank].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
    sorted = [...known, ...unknown];
  } else {
    sorted = [...withoutBlank].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }

  if (hasBlank) sorted.push(BLANK_OPTION);
  return sorted;
};

/**
 * Walks the parsed prospect list once to derive both:
 * - per-field option lists for the multi-select dropdowns, and
 * - a per-row map of raw answers for the multi-select filter to compare
 *   against (avoiding the `formatProspectValue` join that loses
 *   information for array answers and embeds commas in scalar answers
 *   like "Greater than 5k, less than 10k").
 */
export function deriveMultiSelectData(
  parsedProspects: ParsedProspect[],
): MultiSelectData {
  const valuesByField = new Map<string, Set<string>>();
  const rawValuesByRowId = new Map<string, Record<string, unknown>>();

  for (const field of MULTI_SELECT_FIELD_IDS) {
    valuesByField.set(field, new Set());
  }

  for (const prospect of parsedProspects) {
    const rowRaw: Record<string, unknown> = {};

    for (const field of MULTI_SELECT_FIELD_IDS) {
      const raw = readRawAnswer(prospect, field);
      rowRaw[field] = raw;

      const optionSet = valuesByField.get(field);
      if (!optionSet) continue;

      if (isBlank(raw)) {
        optionSet.add(BLANK_OPTION);
        continue;
      }

      if (Array.isArray(raw)) {
        for (const entry of raw) {
          if (typeof entry === "string" && entry.trim() !== "") {
            optionSet.add(entry);
          }
        }
        continue;
      }

      if (typeof raw === "string") {
        optionSet.add(raw);
      }
    }

    rawValuesByRowId.set(prospect.submissionId, rowRaw);
  }

  const optionsByField = new Map<string, string[]>();
  for (const [field, values] of valuesByField) {
    optionsByField.set(field, sortOptions(field, values));
  }

  return { optionsByField, rawValuesByRowId };
}

/**
 * Returns true iff the row's raw answer for `field` matches any of the
 * `selected` options (OR semantics). Scalar string answers match
 * exactly; array answers pass if any entry is in `selected`. Blank
 * answers (null / undefined / empty string / empty array) match only
 * when `(Blank)` is selected.
 */
export function matchesMultiSelect(
  rawValue: unknown,
  selected: readonly string[],
): boolean {
  if (selected.length === 0) return true;

  if (isBlank(rawValue)) {
    return selected.includes(BLANK_OPTION);
  }

  if (Array.isArray(rawValue)) {
    return rawValue.some(
      (entry) => typeof entry === "string" && selected.includes(entry),
    );
  }

  if (typeof rawValue === "string") {
    return selected.includes(rawValue);
  }

  return false;
}
